import React, { useState, useMemo, useEffect } from 'react';
import {
  Table,
  Button,
  Modal,
  OverlayTrigger,
  Tooltip,
  Form,
  InputGroup,
  Row,
  Col,
} from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { BarGraphChart } from '../Charts';
import {
  ChartBarIcon,
  TableIcon,
  SearchIcon,
} from '@heroicons/react/solid';
import { MedicalDocumentsFilters, MEDICAL_DOCUMENTS_FILTERS_TYPES } from '../MedicalDocumentsFilters';
import { Pagination } from '#components';

const LIMIT_PER_PAGE = 50;

// Helper function to get value from biomarker data
const getValue = (value) => {
  if (value.float_value !== null && value.float_value !== undefined) {
    return value.float_value;
  }
  if (value.string_value !== null && value.string_value !== undefined) {
    return value.string_value;
  }
  return 'N/A';
};

// Transform biomarkers data from shared endpoint format to LaboratoryTestsList format
const transformBiomarkersData = (biomarkersData, biomarkerMapping = {}, language = 'pl') => {
  if (!biomarkersData || typeof biomarkersData !== 'object') {
    return [];
  }

  // If biomarkersData is already an array (from shared endpoint), group by biomarker type
  if (Array.isArray(biomarkersData)) {
    
    // Group biomarkers by biomarker ID (or name if available in meta)
    const groupedByBiomarker = biomarkersData.reduce((acc, item) => {
      if (!item || (!item.float_value && !item.string_value)) {
        return acc;
      }
      
      // Get biomarker ID - it can be a number or an object with id property
      const biomarkerId = typeof item.biomarker === 'object' && item.biomarker !== null
        ? item.biomarker.id || item.biomarker.biomarker_id
        : item.biomarker;
      
      // Get biomarker key (name from mapping, meta, or use biomarker ID as fallback)
      // Check if biomarkerMapping is an object with biomarker ID as key, or if it's nested
      const mappingName = biomarkerId && biomarkerMapping[biomarkerId] || 
                         (biomarkerId && biomarkerMapping[biomarkerId]?.name) ||
                         (biomarkerId && typeof biomarkerMapping[biomarkerId] === 'string' ? biomarkerMapping[biomarkerId] : null);
      
      // Get name based on current language (name_pl for Polish, name_en for English)
      const localizedName = language === 'pl' 
        ? (item.meta?.name_pl || item.meta?.name_en)
        : (item.meta?.name_en || item.meta?.name_pl);
      
      const biomarkerKey = 
        mappingName ||
        localizedName ||
        item.meta?.name || 
        item.meta?.biomarker_name || 
        // Extract name from meta.code if it's in format "ID|Name" (e.g., "C55|Erytrocyty")
        (item.meta?.code && typeof item.meta.code === 'string' && item.meta.code.includes('|')
          ? item.meta.code.split('|')[1] // Get part after |
          : null) ||
        (typeof item.biomarker === 'object' && item.biomarker?.name) ||
        (typeof item.biomarker === 'object' && item.biomarker?.biomarker_name) ||
        (biomarkerId ? `Biomarker ${biomarkerId}` : 'Unknown Biomarker');
      
      // Note: effectiveMapping is not available here, we use biomarkerMapping parameter
      
      // Determine if this is a float or string value
      const hasFloatValue = item.float_value !== null && item.float_value !== undefined;
      const hasStringValue = item.string_value !== null && item.string_value !== undefined;
      
      if (!hasFloatValue && !hasStringValue) {
        return acc;
      }
      
      // Create a key that includes the biomarker key and type
      const valueType = hasFloatValue ? 'float' : 'string';
      const groupKey = `${biomarkerKey}|${valueType}`;
      
      if (!acc[groupKey]) {
        acc[groupKey] = {
          key: biomarkerKey,
          type: valueType,
          value: [],
        };
      }
      
      // Add this value to the group
      // Tags can be in meta.document_tags or meta.tags
      const tags = item.meta?.document_tags || item.meta?.tags || [];
      acc[groupKey].value.push({
        float_value: item.float_value,
        string_value: item.string_value,
        date: item.created || item.updated || new Date().toISOString(),
        tags: Array.isArray(tags) ? tags : [],
        document_id: item.document,
        code: item.meta?.code,
      });
      
      return acc;
    }, {});
    
    // Convert grouped object to array format expected by LaboratoryTestsList
    const results = Object.values(groupedByBiomarker);
    return results;
  }

  // Fallback: handle object format with float_values and string_values
  const float_values = biomarkersData.float_values || {};
  const string_values = biomarkersData.string_values || {};

  // Combine float_values and string_values into a single results array
  // Ensure value is an array (it should be, but handle edge cases)
  const results = [
    ...Object.entries(float_values)
      .filter(([key, value]) => Array.isArray(value) && value.length > 0)
      .map(([key, value]) => ({ key, value, type: 'float' })),
    ...Object.entries(string_values)
      .filter(([key, value]) => Array.isArray(value) && value.length > 0)
      .map(([key, value]) => ({ key, value, type: 'string' })),
  ];

  return results;
};

const PublicBiomarkersView = ({ biomarkersData = {}, biomarkerMapping = {}, token }) => {
  const { t, i18n } = useTranslation();
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showStringHistoryModal, setShowStringHistoryModal] = useState(false);
  const [selectedTest, setSelectedTest] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);

  // Transform biomarkers data to the format expected by LaboratoryTestsList logic
  // Use meta.name_pl or meta.name_en based on current language
  const laboratoryTests = useMemo(() => {
    return transformBiomarkersData(biomarkersData, biomarkerMapping, i18n.language);
  }, [biomarkersData, biomarkerMapping, i18n.language]);

  // Group and paginate the tests (same logic as LaboratoryTestsList)
  const paginatedTests = useMemo(() => {
    const groupedTests = laboratoryTests.reduce((acc, test) => {
      if (!Array.isArray(test.value)) {
        return acc;
      }

      test.value.forEach((value) => {
        const valueType =
          value.float_value !== null && value.float_value !== undefined ? 'float' : 'string';
        const key = `${test.key}|${valueType}`;

        // Collect all unique tags from all values
        // const allTags = test.value.reduce((tags, val) => {
        //   if (val.tags && Array.isArray(val.tags)) {
        //     val.tags.forEach((tag) => tags.add(tag));
        //   }
        //   return tags;
        // }, new Set());

        // If we have selected tags, only consider values that match the tags
        // if (selectedTags.length > 0) {
        //   if (!value.tags || !Array.isArray(value.tags) || !selectedTags.some((tag) => value.tags.includes(tag))) {
        //     return;
        //   }
        // }

        // Update the latest value logic to consider tags
        if (!acc[key]) {
          acc[key] = {
            key: test.key,
            type: valueType,
            valueType: valueType,
            latestValue: value,
            // allTags: Array.from(allTags),
            value: test.value,
            originalTest: test,
          };
        } else if (new Date(value.date) > new Date(acc[key].latestValue.date)) {
          // Only update if the new value has a more recent date and matches tag criteria
          acc[key].latestValue = value;
          // Update allTags when latest value changes
          // acc[key].allTags = Array.from(allTags);
        }
      });
      return acc;
    }, {});

    const filteredTests = Object.values(groupedTests).filter((test) => {
      const matchesSearch = test.key.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesSearch;
    });

    const sortedTests = filteredTests.sort((a, b) => {
      const keyComparison = a.key.localeCompare(b.key);
      if (keyComparison !== 0) return keyComparison;
      return a.type === 'float' ? -1 : 1;
    });
    return sortedTests;
  }, [laboratoryTests, searchTerm]);

  const pageNumbers = Math.ceil(paginatedTests.length / LIMIT_PER_PAGE);
  const pages = Array.from(Array(pageNumbers).keys());

  const currentPageTests = useMemo(() => {
    const start = currentPage * LIMIT_PER_PAGE;
    const end = start + LIMIT_PER_PAGE;
    return paginatedTests.slice(start, end);
  }, [paginatedTests, currentPage]);

  const handleShowHistory = (test) => {
    // Filter values based on selected tags if any tags are selected
    // const filteredValues =
    //   selectedTags.length > 0 && Array.isArray(test.value)
    //     ? test.value.filter((v) => v.tags && Array.isArray(v.tags) && selectedTags.some((tag) => v.tags.includes(tag)))
    //     : test.value || [];
    const filteredValues = test.value || [];

    if (test.type === 'float' && Array.isArray(filteredValues)) {
      const chartData = {
        key: test.key,
        label: test.key,
        values: filteredValues
          .map((v) => ({
            date: new Date(v.date),
            value: parseFloat(v.float_value),
            tags: v.tags || [],
          }))
          .filter((v) => !isNaN(v.date.getTime()) && !isNaN(v.value))
          .sort((a, b) => a.date - b.date)
          .map((v) => ({
            date: v.date.toLocaleDateString(),
            value: v.value,
            tags: v.tags,
          })),
      };
      setSelectedTest(chartData);
      setShowHistoryModal(true);
    } else if (test.type === 'string' && Array.isArray(filteredValues)) {
      const tableData = {
        key: test.key,
        values: filteredValues
          .map((v) => ({
            date: new Date(v.date),
            value: v.string_value,
            tags: v.tags || [],
          }))
          .filter((v) => !isNaN(v.date.getTime()))
          .sort((a, b) => b.date - a.date)
          .map((v) => ({
            date: v.date.toLocaleDateString(),
            value: v.value,
            tags: Array.isArray(v.tags) ? v.tags.join(', ') : '',
          })),
      };
      setSelectedTest(tableData);
      setShowStringHistoryModal(true);
    }
  };

  const handleCloseHistoryModal = () => {
    setShowHistoryModal(false);
    setShowStringHistoryModal(false);
    setSelectedTest(null);
  };

  // Reset current page when search term changes
  useEffect(() => {
    setCurrentPage(0);
  }, [searchTerm]);

  const handleTagsChange = (filters) => {
    setSelectedTags(filters.tags || []);
    setCurrentPage(0); // Reset to first page when filters change
  };

  const onPageChange = (p) => setCurrentPage(p);

  if (!laboratoryTests || laboratoryTests.length === 0) {
    return <div className='text-muted text-center py-4'>{t('laboratory-tests.no-tests-found')}</div>;
  }

  return (
    <>
      <div className='mb-3'>
        <Row>
          {/* <Col xs={12} sm={6} className='mb-2 mb-sm-0'>
            <MedicalDocumentsFilters
              filters={{ tags: selectedTags }}
              onChange={handleTagsChange}
              display={[MEDICAL_DOCUMENTS_FILTERS_TYPES.TAGS]}
            />
          </Col> */}
          <Col xs={12} sm={12} className='mb-2 mb-sm-0'>
            <InputGroup>
              <InputGroup.Text>
                <SearchIcon className='icon icon-xs' />
              </InputGroup.Text>
              <Form.Control
                type='text'
                placeholder={t('laboratory-tests.search-placeholder')}
                value={searchTerm}
                onChange={(e) => {
                  const newSearchTerm = e.target.value;
                  setSearchTerm(newSearchTerm);
                }}
              />
            </InputGroup>
          </Col>
        </Row>
      </div>

      {/* Regular table display */}
      <Table striped bordered hover responsive>
        <thead>
          <tr>
            <th>{t('laboratory-tests.table-head.name')}</th>
            <th>{t('laboratory-tests.table-head.value')}</th>
            <th>{t('laboratory-tests.table-head.date')}</th>
            {/* <th>{t('laboratory-tests.table-head.tags')}</th> */}
            <th>{t('laboratory-tests.table-head.history')}</th>
          </tr>
        </thead>
        <tbody>
          {currentPageTests.map((test) => (
            <tr key={`${test.key}-${test.type}`}>
              <td>{test.key}</td>
              <td>{getValue(test.latestValue)}</td>
              <td>
                {test.latestValue.date
                  ? new Date(test.latestValue.date).toLocaleDateString()
                  : 'N/A'}
              </td>
              {/* <td>
                {selectedTags.length > 0
                  ? test.allTags
                      .filter((tag) => selectedTags.includes(tag))
                      .map((tag, index) => (
                        <span key={index} className='badge bg-primary me-1'>
                          {tag}
                        </span>
                      ))
                  : test.allTags?.map((tag, index) => (
                      <span key={index} className='badge bg-primary me-1'>
                        {tag}
                      </span>
                    ))}
              </td> */}
              <td className='text-center'>
                {(test.type === 'float' || test.type === 'string') && (
                  <OverlayTrigger
                    overlay={
                      <Tooltip className='m-0'>
                        {t('laboratory-tests.view-history-tooltip')}
                      </Tooltip>
                    }
                  >
                    <Button
                      variant='link'
                      className='p-0'
                      onClick={() => handleShowHistory(test.originalTest)}
                    >
                      {test.type === 'float' ? (
                        <ChartBarIcon className='icon icon-xs text-gray-500' />
                      ) : (
                        <TableIcon className='icon icon-xs text-gray-500' />
                      )}
                    </Button>
                  </OverlayTrigger>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      {paginatedTests.length > 0 && (
        <div className='px-3 border-0 d-flex flex-column flex-lg-row align-items-center justify-content-between mt-3'>
          <Pagination
            pages={pages}
            limit={LIMIT_PER_PAGE}
            pageNumbers={pageNumbers}
            totalItems={paginatedTests.length}
            currentPage={currentPage}
            onPageChange={onPageChange}
          />
        </div>
      )}

      <Modal show={showHistoryModal} onHide={handleCloseHistoryModal} size='lg'>
        <Modal.Header closeButton>
          <Modal.Title>
            {t('laboratory-tests.history-modal-title', { testName: selectedTest?.key })}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <img src='/text_2.png' alt='Logo' style={{ maxWidth: '100px', marginBottom: '15px' }} />
          {selectedTest && selectedTest.values && selectedTest.values.length > 0 ? (
            <BarGraphChart data={[selectedTest]} />
          ) : (
            <div className='text-muted text-center py-4'>{t('laboratory-tests.chart-invalid-data')}</div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={handleCloseHistoryModal}>
            {t('forms.close')}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showStringHistoryModal} onHide={handleCloseHistoryModal} size='lg'>
        <Modal.Header closeButton>
          <Modal.Title>
            {t('laboratory-tests.history-modal-title', { testName: selectedTest?.key })}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <img src='/text_2.png' alt='Logo' style={{ maxWidth: '100px', marginBottom: '15px' }} />
          {selectedTest && selectedTest.values && selectedTest.values.length > 0 ? (
            <Table striped bordered hover>
              <thead>
                <tr>
                  <th>{t('laboratory-tests.history-modal-date')}</th>
                  <th>{t('laboratory-tests.history-modal-value')}</th>
                  <th>{t('laboratory-tests.table-head.tags')}</th>
                </tr>
              </thead>
              <tbody>
                {selectedTest.values.map((item, index) => (
                  <tr key={index}>
                    <td>{item.date}</td>
                    <td>{item.value}</td>
                    <td>{item.tags}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <div className='text-muted text-center py-4'>{t('laboratory-tests.no-data')}</div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={handleCloseHistoryModal}>
            {t('forms.close')}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default PublicBiomarkersView;

